import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { FeatherIcon } from '../components/FeatherIcon'

export interface SelectOption<T extends string> {
  value: T
  label: string
  disabled?: boolean
}

const MENU_MAX = 320
const GAP = 4

/**
 * The page's own dropdown (no native <select> menus). The menu floats in a
 * portal so side sheets and cards never clip it; it opens upward when there
 * is no room below. Arrow keys, Home/End, Enter, Escape and type-ahead work.
 */
export function Select<T extends string>({
  value,
  options,
  onChange,
  label,
  placeholder,
  disabled = false,
  title,
  className = '',
}: {
  value: T | ''
  options: SelectOption<T>[]
  onChange: (value: T) => void
  /** Accessible name (shown nowhere else). */
  label: string
  /** Shown when nothing is chosen. */
  placeholder?: string
  disabled?: boolean
  title?: string
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number; maxHeight: number } | null>(null)
  const button = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const typed = useRef({ text: '', at: 0 })
  const listId = useId()
  const current = options.find((o) => o.value === value)

  const place = () => {
    const r = button.current?.getBoundingClientRect()
    if (!r) return
    const below = window.innerHeight - r.bottom - GAP - 8
    const above = r.top - GAP - 8
    const width = Math.min(Math.max(r.width, 180), window.innerWidth - 16)
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8))
    const want = Math.min(MENU_MAX, options.length * 34 + 10)
    if (below >= want || below >= above) setPos({ left, width, top: r.bottom + GAP, maxHeight: Math.min(MENU_MAX, below) })
    else setPos({ left, width, bottom: window.innerHeight - r.top + GAP, maxHeight: Math.min(MENU_MAX, above) })
  }

  const show = (at = options.findIndex((o) => o.value === value)) => {
    if (disabled) return
    place()
    setActive(at >= 0 ? at : firstEnabled(options, 0, 1))
    setOpen(true)
  }
  const hide = (focus = true) => {
    setOpen(false)
    if (focus) button.current?.focus()
  }
  const pick = (i: number) => {
    const o = options[i]
    if (!o || o.disabled) return
    hide()
    if (o.value !== value) onChange(o.value)
  }

  useLayoutEffect(() => {
    if (open) place()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-place when the options change while open
  }, [open, options.length])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (!menu.current?.contains(target) && !button.current?.contains(target)) hide(false)
    }
    const onMove = (e: Event) => {
      if (!menu.current?.contains(e.target as Node)) place()
    }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers read refs only
  }, [open])

  useEffect(() => {
    if (open && active >= 0) menu.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView?.({ block: 'nearest' })
  }, [open, active])

  const onKey = (e: KeyboardEvent) => {
    const move = (from: number, step: 1 | -1) => setActive(firstEnabled(options, from, step))
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault()
        show()
      }
      return
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        move(active + 1, 1)
        break
      case 'ArrowUp':
        e.preventDefault()
        move(active - 1, -1)
        break
      case 'Home':
        e.preventDefault()
        move(0, 1)
        break
      case 'End':
        e.preventDefault()
        move(options.length - 1, -1)
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        pick(active)
        break
      case 'Escape':
        // preventDefault tells the surrounding sheet not to close as well.
        e.preventDefault()
        hide()
        break
      case 'Tab':
        hide(false)
        break
      default:
        if (e.key.length === 1) {
          const now = Date.now()
          typed.current = { text: (now - typed.current.at < 700 ? typed.current.text : '') + e.key.toLocaleLowerCase(), at: now }
          const i = options.findIndex((o) => !o.disabled && o.label.toLocaleLowerCase().startsWith(typed.current.text))
          if (i >= 0) setActive(i)
        }
    }
  }

  return (
    <>
      <button
        ref={button}
        type="button"
        className={`dl-select dl-dd ${className}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={label}
        title={title}
        disabled={disabled}
        onClick={() => (open ? hide() : show())}
        onKeyDown={onKey}
      >
        <span className={`dl-ellipsis ${current ? '' : 'dl-dd-placeholder'}`}>{current?.label ?? placeholder ?? ''}</span>
        <FeatherIcon name="chevron-down" size={14} className="dl-dd-caret" />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menu}
            id={listId}
            className="dl-float dl-dd-menu"
            role="listbox"
            aria-label={label}
            style={{ left: pos.left, minWidth: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
            onKeyDown={onKey}
          >
            {options.map((o, i) => (
              <div
                key={o.value}
                data-i={i}
                role="option"
                aria-selected={o.value === value}
                aria-disabled={o.disabled || undefined}
                className={`dl-dd-item ${o.value === value ? 'is-on' : ''} ${i === active ? 'is-active' : ''} ${o.disabled ? 'is-disabled' : ''}`}
                onMouseEnter={() => !o.disabled && setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(i)}
              >
                <span className="dl-ellipsis">{o.label}</span>
                {o.value === value && <FeatherIcon name="check" size={14} className="dl-dd-check" />}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  )
}

function firstEnabled<T extends string>(options: SelectOption<T>[], from: number, step: 1 | -1): number {
  for (let i = from; i >= 0 && i < options.length; i += step) if (!options[i]!.disabled) return i
  // Nothing further that way: stay on the last enabled one in the other direction.
  for (let i = from - step; i >= 0 && i < options.length; i -= step) if (!options[i]!.disabled) return i
  return -1
}
